import {Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild, ViewEncapsulation} from '@angular/core';
import {forkJoin, Observable, of, Subject} from "rxjs";
import {catchError, debounceTime, finalize, first, switchMap, takeUntil, tap} from "rxjs/operators";
import {SeriesDataService} from "../../services/series_data.service";
import {EventType} from '../../_models/event-type';
import {ConstantProperty} from '../../_models/constant-property';
import {ApiService} from '../../services/api/api.service';
import {ComponentType} from "../../_models/component-type";
import {animate, state, style, transition, trigger} from "@angular/animations";
import {deepCopy, guid} from "../../lib/utils";
import {SearchQueryOptions} from '../../services/api/search-query-options';
import {ListResponse, SingleResponse} from '../../services/api/response-types';
import {MatPaginator} from '@angular/material/paginator';
import {MatSort} from '@angular/material/sort';
import {PaginationDataSourceWithAddRow} from "../../services/api/pagination-data-source-with-add-row";
import {ConstantPropertyTableFormService} from "./constant-property-table-service.service";
import {SaveService} from "../../services/save/save.service";
import {RelationshipApiMappingService} from "../../data/relationship-api-mapping.service";
import {RelMappingResult} from "../../_typing/relationship-api-mapping";
import {NotificationService} from "../../services/notification.service";
import {TOOLTIP_SHOW_DELAY} from "../../shared/globals";
import {StringFormatType} from "../../_typing/config/string-format-type";
import {SelectManySearchBaseComponent} from "../../components/select-search-api/many/select-many-search-base.component";
import {UtilsBarComponent} from "../../tables/utils-bar/utils.bar.component";
import {COMMON_TABLE_IMPORTS} from "../../shared/import-groups/Common";
import {SharedPipesModule} from "../../shared/modules/shared-pipes.module";
import {SharedModule} from "../../shared/modules/shared-module.module";
import {StringFormatSelectorComponent} from "../../components/string-format-selector/string-format-selector.component";
import {FormulaBuilderComponent} from "../../components/formula-builder/formula-builder.component";
import {
    AuditMixinIndicatorComponent
} from "../../components/audit-mixin/audit-mixin-indicator/audit-mixin-indicator.component";
import {ColumnFormatsDict} from "../../services/table-utils.service";
import {ColumnResizeDirective} from "../../directives/column-resize.directive";

export type CONSTANT_PROPERTY_RELATED_TYPE = EventType | ComponentType;

@Component({
    selector: 'constant-property-table-form',
    templateUrl: './constant-property-table-form.component.html',
    styleUrls: ['./constant-property-table-form.component.scss',
        '../../components/series-audit-history/series-audit-history.component.scss'],
    encapsulation: ViewEncapsulation.None,
    providers: [ConstantPropertyTableFormService],
    animations: [
        trigger('detailExpand', [
            state('collapsed', style({height: '0px', minHeight: '0'})),
            state('expanded', style({height: '*'})),
            transition('expanded <=> collapsed', animate('225ms cubic-bezier(0.4, 0.0, 0.2, 1)')),
        ]),
    ],
    imports: [
        SelectManySearchBaseComponent,
        UtilsBarComponent,
        COMMON_TABLE_IMPORTS,
        SharedPipesModule,
        SharedModule,
        StringFormatSelectorComponent,
        FormulaBuilderComponent,
        AuditMixinIndicatorComponent,
        ColumnResizeDirective
    ],
})
export class ConstantPropertyTableFormComponent implements OnInit, OnDestroy {
    selected_properties: ConstantProperty[];
    old_selected_properties: ConstantProperty[];
    selected_properties_filter: any[];
    selected_properties_reverse_filter: any[];
    constant_property_parent_list: any[];
    @Input() hide_select_list = false;
    @Output() cp_saved: EventEmitter<{ constant_property: ConstantProperty, new: boolean }> = new EventEmitter();
    @Output() prop_list_changed: EventEmitter<any[]> = new EventEmitter();
    @Output() columnWidthsChanged: EventEmitter<{'property_table_formats':Partial<ColumnFormatsDict>}> = new EventEmitter();

    private $property_list_data: Subject<string> = new Subject<string>();
    private readonly onDestroy = new Subject<void>();

    cp_editing_dict = {};
    constant_property_types = ['string', 'float', 'datetime', 'file', 'user', 'user_group', 'account'];
    constant_property_aggregations = ['total', 'mean', 'count', 'min', 'max'];

    filter_string = '';
    @ViewChild(MatSort) sort: MatSort;
    @ViewChild(MatPaginator) paginator: MatPaginator;
    dataSource: PaginationDataSourceWithAddRow<ConstantProperty>;
    columns = ['edit', 'name', 'description', 'alias', 'formula', 'is_drop_down_list', 'data_type', 'is_calculation', 'aggregation', 'name_format', 'json'];
    expandedElement: any | null = null;
    pageSize: number = 10;

    private _columnWidthDict: Partial<ColumnFormatsDict>;
    @Input() set columnWidthDict(columnWidthDict:Partial<ColumnFormatsDict>) {
        if (!columnWidthDict) {
            this._columnWidthDict = {
                'edit': {allow_resize: true, width: null},
                'name': {allow_resize: true, width: null},
                'description': {allow_resize: true, width: null},
                'alias': {allow_resize: true, width: null},
                'formula': {allow_resize: true, width: null},
                'is_drop_down_list': {allow_resize: true, width: null},
                'data_type': {allow_resize: true, width: null},
                'is_calculation': {allow_resize: true, width: null},
                'aggregation': {allow_resize: true, width: null},
                'name_format': {allow_resize: true, width: null},
                'json': {allow_resize: true, width: null}
            }
        }
        else {
            this._columnWidthDict = columnWidthDict;
        }
    }

    get columnWidthDict(): Partial<ColumnFormatsDict> {
        return this._columnWidthDict;
    }

    constructor(public seriesData: SeriesDataService,
                private api: ApiService,
                private notification: NotificationService,
                private propertyService: ConstantPropertyTableFormService,
                private saveService: SaveService,
                private relMapping: RelationshipApiMappingService) {
    }

    _parent_component: CONSTANT_PROPERTY_RELATED_TYPE;

    get parent_component(): CONSTANT_PROPERTY_RELATED_TYPE {
        return this._parent_component;
    }

    @Input()
    set parent_component(component: CONSTANT_PROPERTY_RELATED_TYPE) {
        this._parent_component = component;
    }

    ngOnInit() {
        const initialQuery = new SearchQueryOptions();
        initialQuery.page_number = 1;
        initialQuery.sort = 'name';
        setTimeout(() => {
            this.paginator.pageSize = this.pageSize;
            initialQuery.filters = this.propertyService.refreshPropertyQuery(this.parent_component);

            this.dataSource = new PaginationDataSourceWithAddRow<ConstantProperty>(
                (query) => this.page(query),
                initialQuery,
                this.paginator,
                this.sort
            );
        });
        this.$property_list_data.pipe(debounceTime(1000), takeUntil(this.onDestroy)).subscribe(() => {
            this.emitFilterQuery();
        });
        this.expandedElement = null;

        /**For the dropdown/select list**/
        const options = new SearchQueryOptions();
        options.filters = this.propertyService.refreshPropertyQuery(this.parent_component);
        this.selected_properties_filter = options.filters;
        this.selected_properties_reverse_filter = this.propertyService.getConstantPropertiesForParentReverseFilter(this.parent_component);
        const $props = this.api.constant_property_light.searchMany(options).pipe(tap(result => {
            this.selected_properties = result.data;
            this.old_selected_properties = deepCopy(result.data);
        }))
        const $prop_parent = this.propertyService.getConstantPropertyParentTypes(this.parent_component)
            .pipe(tap(result => {
                this.constant_property_parent_list = result.data;
            }))
        forkJoin([$props, $prop_parent]).pipe(first(), takeUntil(this.onDestroy))
            .subscribe();

        this.saveService.getSaveHandle().pipe(takeUntil(this.onDestroy)).subscribe(() => {
            console.log('INFO: Saving constant properties for ' + this.parent_component?.attributes?.name);
            this.saveMany();
        });
    }

    page(query: SearchQueryOptions): Observable<ListResponse<any>> {
        return this.api.constant_property_formula_light.searchMany(query).pipe(
            first(), takeUntil(this.onDestroy)
        )
    }

    emitFilterQuery() {
        this.paginator.pageIndex = 0;
        const filters = this.propertyService.refreshPropertyQuery(this.parent_component);
        this.dataSource.filterBy(filters);
    }

    updateSearchFilter() {
        this.paginator.pageIndex = 0;
        const filters = this.propertyService.updateSearchFilter(this.parent_component, this.filter_string);
        this.dataSource.filterBy(filters)
    }

    updateSort(event) {
        this.paginator.pageIndex = 0;
        this.dataSource.sortBy(this.sort);
    }

    newCP: ConstantProperty;

    newConstantProperty() {
        if (this.newCP) {
            this.notification.openWarning('Please complete and save the current New Constant Property.');
            return;
        }
        this.newCP = new ConstantProperty();
        this.newCP.id = 'temp' + guid()
        this.dataSource.addRow(this.newCP);
        this.editConstantProperty(this.newCP);
    }

    removeConstantProperty(constant_property) {
        this.dataSource.clearTempRows();
        this.newCP = null;
    }

    setNameFormat($event: StringFormatType, constantProperty: ConstantProperty): void {
        if (!this.cp_editing_dict[constantProperty.id]) return;
        constantProperty.attributes.name_format = $event;
    }

    checkAllowNameFormat(constantProperty: ConstantProperty): boolean {
        return !(this.cp_editing_dict[constantProperty.id] && constantProperty.attributes.data_type === 'string'
            && !constantProperty.attributes.is_drop_down_list && !constantProperty.attributes.is_calculation);
    }

    editConstantProperty(constant_property) {
        if (this.cp_editing_dict[constant_property.id]) {
            this.cp_editing_dict[constant_property.id] = false;
            this.expandedElement = null;
            return;
        }
        this.selected_properties.forEach(cp => {
            if (cp.id !== constant_property.id && !cp.id.includes('temp')) {
                this.cp_editing_dict[cp.id] = false;
            }
        });
        if (!constant_property.id.includes('temp')) {
            this.cp_editing_dict[constant_property.id] = true;
        } else {
            this.cp_editing_dict[constant_property.id] = !this.cp_editing_dict[constant_property.id];
        }
    }

    saveConstantProperty(constant_property) {
        const ctrl = this;
        let $upsert;
        const temp_id = constant_property.id;
        delete constant_property.attributes.formula;

        if (!this.validate(constant_property)) return;
        if (temp_id.includes('temp')) {
            $upsert = this.api.constant_property.obsSave(constant_property);
        } else {
            $upsert = this.api.constant_property.obsPatch(constant_property);
        }
        $upsert.pipe(
            switchMap((result: SingleResponse<ConstantProperty>) => {
                let $link: Observable<any>;
                if (temp_id.includes('temp')) {
                    $link = this.getParentConstantPropertyRelationshipObs(result.data).pipe(tap((ct_cp) => {
                        this.notification.openSuccess(`Constant property linked to ${this.parent_component.attributes.name}.`);
                        this.newCP = null;
                        this.constant_property_parent_list.push(ct_cp.data);
                        this.old_selected_properties.push(result.data);
                    }));
                } else {
                    $link = of([]);
                }
                constant_property = result.data;

                ctrl.cp_editing_dict[temp_id] = false;
                ctrl.cp_editing_dict[constant_property.id] = false;
                ctrl.expandedElement = null;
                this.notification.openSuccess('Constant property saved.');
                return $link;
            }),
            catchError((err: any, caught: any) => {
                const error = err?.error?.errors?.length > 0 && err.error.errors[0].detail ?
                    err.error.errors[0].detail.substring(0, 120) + '...(Please see console).' : '';
                this.notification.openError(`Error saving constant property. ${error}`);

                if (temp_id) {
                    constant_property.id = temp_id;
                }
                return err;
            }),
            first(), takeUntil(this.onDestroy))
            .subscribe(() => {
                if (temp_id.includes('temp')) {
                    this.emitFilterQuery();
                }
            });
    }

    saveMany() {
        const model = 'constant_property_' + this.parent_component.type;
        this.relMapping.saveMany(model, this.parent_component.type, this.parent_component.id, 'constant_property',
            this.selected_properties, this.old_selected_properties, this.constant_property_parent_list)
            .pipe(tap((result: RelMappingResult) => {
                    this.constant_property_parent_list.push(...result.successes.map(s => s.data));
                    if (result.deletedIds.length) {
                        const deletedSet = new Set(result.deletedIds);
                        this.constant_property_parent_list = this.constant_property_parent_list.filter(item => !deletedSet.has(item.id));
                    }
                    if (result.failures.length > 0) {
                        console.log("ERROR: ConstantPropertyTableForm (saveMany) ", result.failures);
                        this.notification.openError(`${result.successes.length} saved. ${result.failures.length} property link(s) failed to save. Please see the console for details.`);
                    }
                }),
                /**Dont close subscription when form closes (onDestroy), only closes on finalise**/
                finalize(() => {
                    // constant_property_parent_list already reflects server truth after the tap
                    // (successful adds pushed, successful deletes filtered out, failures untouched).
                    // Derive old_selected_properties from those links so a partial failure leaves
                    // the unsaved row in the next diff to be retried on the next Save.
                    const cpsById = new Map<string, ConstantProperty>();
                    [...this.selected_properties, ...this.old_selected_properties].forEach(cp => cpsById.set(cp.id, cp));
                    this.old_selected_properties = this.constant_property_parent_list
                        .map(link => cpsById.get(link.relationships.constant_property.data.id))
                        .filter(cp => !!cp);
                    console.log('INFO: Sequence complete');
                }))
            .subscribe(() => {
            });
    }

    getParentConstantPropertyRelationshipObs(constant_property: ConstantProperty): Observable<any> {
        let obj: any = this.propertyService.getParentConstantPropertyRelationshipObject(this.parent_component, constant_property);
        const model = 'constant_property_' + this.parent_component.type;
        return this.api[model].obsSave(obj).pipe(tap(result => {
            this.selected_properties.push(constant_property)
        }));
    }

    validate(constant_property: ConstantProperty): boolean {
        return this.validateJson(constant_property) && this.validateCalculation(constant_property);
    }

    validateCalculation(constant_property): boolean {
        if (!constant_property.attributes.is_calculation) {
            return true;
        }
        if (constant_property.attributes.name_formula) {
            return true;
        } else {
            this.notification.openError('Constant properties of type calculation must have a formula.');
        }
    }

    validateJson(constant_property): boolean {
        if (!constant_property.attributes.is_drop_down_list) {
            return true;
        }
        try {
            constant_property.attributes.json = JSON.parse(constant_property.attributes.json);
            return true;
        } catch (e) {
            const item = constant_property.attributes.json;
            if ((typeof item === "object" || Array.isArray(item)) && item !== null) {
                return true;
            }
            this.notification.openError('Invalid json. Please correct this and try again.');
            return false;
        }
    }

    saveColumnWidths() {
        this.columnWidthsChanged.emit({property_table_formats: this.columnWidthDict});
    }

    ngOnDestroy(): void {
        this.onDestroy.next();
        this.onDestroy.unsubscribe();
    }

    protected readonly TOOLTIP_SHOW_DELAY = TOOLTIP_SHOW_DELAY;
}
